import { text } from '#contract'
import { useView } from '../app/use-view.ts'
import { useT } from '../i18n/index.ts'
import { appName, appsFor } from './apps/index.ts'
import type { OsApp } from './apps/index.ts'
import styles from './launcher.module.css'
import { rectInOs } from './motion.ts'
import type { OsState } from './os-state.ts'

/**
 * Where apps start: one list of the form's apps in two parts. The phone has a grid of
 * icons with labels, page dots and its pinned apps in a glass dock; the desktop has only
 * the dock, with every app, magnifying under the pointer and a dot under running ones.
 */
export function Launcher({ state }: { readonly state: OsState }) {
  const t = useT()
  const phone = state.form === 'phone'
  const apps = appsFor(state.form)
  const windows = state.windows.value
  // On the phone an app covers the home screen: out of reach until it goes home, when the
  // home screen settles back in (a fresh mount replays its entrance).
  const covered = phone && state.front.value !== null
  const docked = phone ? apps.filter((app) => app.pinned === true) : apps

  return (
    <div key={covered ? 'covered' : 'home'} class={styles.launcher} inert={covered}>
      {phone && (
        <>
          <nav class={styles.grid} aria-label={t(text('os.apps'))}>
            {apps.map((app) => (
              <LaunchButton key={app.id} app={app} state={state} labelled />
            ))}
          </nav>
          <div class={styles.dots} aria-hidden="true">
            <span class={styles.dot} data-current />
            <span class={styles.dot} />
          </div>
        </>
      )}
      <nav class={[styles.dock, 'os-glass'].join(' ')} aria-label={t(text('os.dock'))}>
        {docked.map((app) => (
          <LaunchButton
            key={app.id}
            app={app}
            state={state}
            running={!phone && windows.some((entry) => entry.app === app.id)}
          />
        ))}
      </nav>
    </div>
  )
}

interface LaunchButtonProps {
  readonly app: OsApp
  readonly state: OsState
  /** The phone grid's: the name under the icon. The dock shows a tooltip instead. */
  readonly labelled?: boolean
  readonly running?: boolean
}

function LaunchButton({ app, state, labelled = false, running = false }: LaunchButtonProps) {
  const t = useT()
  const name = t(appName(app.id))
  const badge = useView((view) => app.badge?.(view) ?? 0)
  const Icon = app.icon
  return (
    <button
      type="button"
      class={labelled ? styles.app : styles.docked}
      data-app={app.id}
      aria-label={name}
      onClick={(event) => {
        // The app zooms out of the icon, wherever it is right now.
        const icon = event.currentTarget.querySelector('svg')
        state.open(app.id, icon ? rectInOs(icon) : null)
      }}
    >
      <Icon class={styles.icon} />
      {badge > 0 && (
        <span class={styles.badge} aria-label={t(text('os.badge', { n: badge }))}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {labelled ? (
        <span class={styles.label} aria-hidden="true">
          {name}
        </span>
      ) : (
        <span class={styles.tooltip} aria-hidden="true">
          {name}
        </span>
      )}
      {running && <span class={styles.running} />}
    </button>
  )
}
